// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// Recent-searches memo hook. Persists the last N unique identifiers typed into
// the search box via localStorage so they survive page reloads within the same
// browser/profile. The store is scoped per AU (key prefix) to avoid colliding
// with other AUs that may share the same origin in a Zoho Creator embed.

import { useCallback, useMemo, useState } from "react";

const STORAGE_KEY = "au0009:recent-searches";
const MAX_ITEMS = 10;

function loadFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((v): v is string => typeof v === "string").slice(0, MAX_ITEMS);
    }
    return [];
  } catch {
    return [];
  }
}

function saveToStorage(items: readonly string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Storage full or unavailable — silently degrade.
  }
}

export interface RecentSearches {
  /** Ordered most-recent-first, max 10. */
  readonly items: readonly string[];
  /** Record a new search; moves it to the front, deduplicates, caps at 10. */
  add(identifier: string): void;
  /** Remove a single entry. */
  remove(identifier: string): void;
  /** Clear all remembered searches. */
  clear(): void;
}

export function useRecentSearches(): RecentSearches {
  const [items, setItems] = useState<string[]>(loadFromStorage);

  const add = useCallback((identifier: string) => {
    setItems((prev) => {
      const next = [identifier, ...prev.filter((v) => v !== identifier)].slice(0, MAX_ITEMS);
      saveToStorage(next);
      return next;
    });
  }, []);

  const remove = useCallback((identifier: string) => {
    setItems((prev) => {
      const next = prev.filter((v) => v !== identifier);
      saveToStorage(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    saveToStorage([]);
  }, []);

  return useMemo(() => ({ items, add, remove, clear }), [items, add, remove, clear]);
}
